/* =============================================================================
   VIEWS / ADMIN / BAR — Vollbild-Theken-Display für Getränke
   =============================================================================
   v0.30.0 / Session 20 / Phase 16.3 (User-Request P2)
   ----------------------------------------------------------------------------
   Parallel-View zum KDS, aber für die Theke / den Tresen. Zeigt nur
   Getränke-Positionen aller offenen Bestellungen. Pattern fast 1:1 KDS,
   eigene Hash-Route '#bar' und Hotkey Cmd/Ctrl+Shift+B.

   Item-Filter:
     - Kategorie 'Getränke' (case-insensitiv)
     - Heuristik-Fallback: name enthält 'cola', 'bier', 'wein', 'wasser', 'saft',
       'kaffee', 'tee', 'softdrink' etc., falls keine Category bekannt.

   "Gepoured"-Tracking:
     - Pro Item-ID wird ein lokaler localStorage-Flag gesetzt, sobald der
       Theken-Mitarbeiter "Eingegossen" tippt. Reine UI-Hilfe, ändert NICHT
       den Order-Status (das macht weiterhin die Küche oder Theke kann auch
       die "Bereit"-Order-Aktion auslösen, falls Drinks parallel laufen).
     - Storage-Key: `gg_bar_poured_v1` → Map<itemId, ISO-Timestamp>
     - Aufgeräumt: Items, die nicht mehr im aktiven Order-Set sind, werden
       beim nächsten Render aus dem localStorage entfernt.

   Status-Coupling:
     - Großer Footer-Button pro Order erlaubt direkten Status-Sprung
       (analog KDS-Next-Button) — koordiniert mit dem KDS, weil die
       Order-Status-Transitions idempotent sind (RPC).
============================================================================ */

import { htmlEscape, showToast, formatCustomizationsByCategory, dateToISO } from '../../utils/helpers.js';
import { colorForOrder, computeOrderTimer, isDrinkItem, isDisplayFieldVisible } from '../../core/display-config.js';
import {
  getCachedItems, fetchItemsFor, batchFetchItemsFor, invalidateItems, groupItemsByRound,
  hasCachedRoundStatus, hasPendingRoundStatus, fetchRoundStatusFor, batchFetchRoundStatusFor,
  invalidateRoundStatus, roundChannelStatus
} from '../../core/order-items-cache.js';
import { renderConfiguredFields } from './kds.js';
import { renderOperatorBadges } from './operator-ui.js';
// S253 — dieselben Küchen-Hinweise wie im KDS: wer eine Allergie angibt, meint
// auch das Getränk (Milch, Nüsse im Sirup). Die Theke bekommt sie deshalb an
// derselben Stelle und nach derselben Regel.
import { guestNotes, notesForRound, renderGuestNotesHtml, initGuestNotes } from './table-guest-notes.js';

// S253 — Abmelder des Hinweis-Abos (nur einmal verdrahten).
let _barNotesOff = null;

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

const BAR_MASTER_STATUSES = ['pending', 'confirmed', 'preparing', 'ready'];

const STATUS_LABEL = {
  pending:    'Neu',
  confirmed:  'Angenommen',
  preparing:  'In Zubereitung',
  ready:      'Bereit',
  picked_up:  'Abgeholt'
};

// S139 P5 — Channel-Action-Logik jetzt PRO RUNDE (Runde × Kanal=bar), Spiegel
// von kds.js#nextKitchenAction. `roundStatus` = Theken-Status DIESER Runde.
export function nextBarAction(order, roundStatus) {
  if (!order) return null;
  const m = order.status;
  if (m === 'cancelled' || m === 'picked_up') return null;
  if (m === 'pending') {
    return { kind: 'master', status: 'confirmed', label: '✓ Annehmen' };
  }
  const b = roundStatus || 'pending';
  if (b === 'pending')   return { kind: 'channel', channel: 'bar', status: 'preparing', label: '▶ Zubereiten' };
  if (b === 'preparing') return { kind: 'channel', channel: 'bar', status: 'ready',     label: '🥤 Bereit' };
  // Phase 19.4: Theke löst KEIN 'picked_up' aus — analog KDS.
  if (b === 'ready') return null;
  return null;
}

function barStatusLabel(order, roundStatus) {
  const b = roundStatus || 'pending';
  if (b === 'pending' && order.status && order.status !== 'pending') {
    return STATUS_LABEL.confirmed;
  }
  return STATUS_LABEL[b] || b;
}

// S139 P5 — Counter-Bucket EINER Theken-Runden-Kachel (Pure-Helper, Spiegel
// von kds.js#kdsCountBucket für den bar-Kanal).
export function barCountBucket(masterStatus, roundStatus) {
  const b = roundStatus || 'pending';
  if (b === 'preparing') return 'preparing';
  if (b === 'ready')     return 'ready';
  if (masterStatus === 'pending') return 'pending';
  return 'confirmed';
}

// — Items-Cache — S104-B: gemeinsames Item-Access-Modul (core/order-items-cache.js).
//   Ersetzt den Theken-eigenen `_itemsCache`/Batch-Loader (EINE Quelle).
// (getCachedItems/fetchItemsFor/batchFetchItemsFor/invalidateItems importiert.)

// — Drink-Filter (zentral in display-config.js, hier nur Wrapper) ————
// Aus DRY-Gründen: KDS und Bar nutzen denselben Helper aus core/display-config.
// Die alte lokale Funktion + DRINK_NAME_REGEX sind nach F6 (Session 20) entfallen.

// Pour-Tracking (lokales localStorage) wurde in F7a (Session 20 Funktionstest)
// entfernt — die Bar nutzt jetzt den gleichen Order-Status-Flow wie das KDS.
// Eine Bestellung wandert über Annehmen → Zubereiten → Bereit → Abgeholt.

// — Helpers ————————————————————————————————
function minutesSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}
// waitColor wird jetzt über display-config gesteuert — siehe colorForOrder('bar', …).
// Default-Fallback bleibt erhalten für Aufrufe ohne Order-Kontext.
function waitColor(mins) {
  if (mins < 3)  return 'wait-green';
  if (mins < 6)  return 'wait-yellow';
  return 'wait-red';
}
function fmtTime(t) { return String(t || '').slice(0, 5); }

/** Kategorie eines Items ermitteln — Spiegel zu kds.js#detectCategory. */
function detectCategory(item) {
  if (item.categorySnapshot) return String(item.categorySnapshot);
  if (item.category_snapshot) return String(item.category_snapshot);
  if (item.category) return String(item.category);
  if (item.menuCategory) return String(item.menuCategory);
  const state = getState();
  const menu = state.menu || state.menuItems || (state.settings && state.settings.menuItems) || [];
  if (Array.isArray(menu)) {
    const mi = menu.find(m => m.id === item.menuItemId || m.id === item.menu_item_id);
    if (mi && mi.category) return String(mi.category);
  }
  return '';
}

// UX-Win S24: Heute-Filter analog zu kds.js. Modul-State (session-only).
let _barShowAllDates = false;
// S154 — Theken-Kacheln mit aufgeklappter Bauanleitung (on-demand, render-fest).
const _barBuildExpanded = new Set();

// S154 — nummerierte Bauanleitung (add grün, remove durchgestrichen) für die Theke.
function renderBarBuildHtml(steps) {
  if (!steps || !steps.length) {
    return `<div class="bar-build-empty hint" style="font-size:.72rem; margin-top:2px;">Keine Bauanleitung hinterlegt.</div>`;
  }
  // S154b — Stapel-Optik (bottom/top als Boden/Deckel bündig, middle eingerückt).
  const row = s => {
    const bun = s.layer === 'bottom' || s.layer === 'top';
    const indent = (s.layer === 'middle') ? 'margin-left:18px;' : '';
    const bunStyle = bun ? 'background:#f0e2c8; border-radius:12px; font-weight:600; padding:2px 10px;' : 'padding:1px 8px;';
    const kindStyle = s.kind === 'remove' ? 'text-decoration:line-through; color:#b94a48;'
                    : s.kind === 'add' ? 'color:#2f6b3a; font-weight:600;' : '';
    const st = s.station ? ` <span class="hint">· ${htmlEscape(s.station)}</span>` : '';
    return `<div style="${indent}${bunStyle}${kindStyle} margin-top:2px;">${htmlEscape(s.text)}${st}</div>`;
  };
  // S154b/c — Burger-Querschnitt: Deckel (top) oben, Boden (bottom) unten. Daten
  // bleiben Aufbau-Reihenfolge; nur die Darstellung wird gespiegelt.
  return `<div class="bar-build" style="margin:4px 0 0; font-size:.74rem; color:#555;">${steps.slice().reverse().map(row).join('')}</div>`;
}

export function barToggleDateFilter() {
  _barShowAllDates = !_barShowAllDates;
  renderBar();
}

// S60 FIX.3 — Pre-Order-Window: synchron zu KDS. Siehe kds.js für die Begründung.
const PREORDER_WINDOW_MS = 60 * 60 * 1000;

function pickupAtMs(o) {
  if (!o || !o.pickupDate) return null;
  const [y, m, d] = String(o.pickupDate).split('-').map(Number);
  const [h, mi]   = String(o.pickupTime || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, h || 0, mi || 0, 0, 0).getTime();
}

function activeOrders() {
  let orders = (getState().orders || []).filter(o => BAR_MASTER_STATUSES.includes(o.status));
  // S60 FIX.3 — Zeitfenster-Filter (Jonas-Spec): 60-Min-Pre-Order-Fenster.
  // „Alle"-Toggle hebt den Cutoff auf und zeigt Zukunfts-Vorbestellungen.
  if (!_barShowAllDates) {
    const cutoff = Date.now() + PREORDER_WINDOW_MS;
    const today = dateToISO(new Date());
    orders = orders.filter(o => {
      const ms = pickupAtMs(o);
      if (ms === null) return true;
      // S100-B — Vortags-Pipeline raus: Theke ist heute-fokussiert; die offenen
      // Altlasten fängt die Carry-Over-Sektion im operativen Tab (S100-A).
      // Nur zusammen mit S100-A mergen, sonst Datensatz-Verlust ohne Netz.
      if (o.pickupDate < today) return false;
      return ms <= cutoff;
    });
  }
  // Sortierung synchron zur KDS umgestellt im Funktionstest Audit-S21
  // (Jonas-Spec): "rechts = was als nächstes raus muss". Siehe Begründung
  // in src/views/admin/kds.js#activeOrders — identische Strategie:
  //   1. pickup_time absteigend (späteste links, dringendste rechts)
  //   2. status-Rank aufsteigend nur als Tiebreaker bei IDENTISCHER pickup_time
  //   3. createdAt absteigend als zweiter Tiebreaker
  const STATUS_RANK_FOR_TIEBREAK = { pending: 0, confirmed: 1, preparing: 2, ready: 3 };
  return orders.slice().sort((a, b) => {
    const aKey = String(a.pickupDate || '') + ' ' + String(a.pickupTime || '00:00');
    const bKey = String(b.pickupDate || '') + ' ' + String(b.pickupTime || '00:00');
    if (aKey !== bKey) return bKey.localeCompare(aKey);
    const r = (STATUS_RANK_FOR_TIEBREAK[a.status] || 0) - (STATUS_RANK_FOR_TIEBREAK[b.status] || 0);
    if (r !== 0) return r;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
}

// — Tile-Renderer ————————————————————————————
// S139 P5 — eine Kachel je (Order, Runde-mit-Getränken). Spiegel von kds.js.
function renderTile(o, round, drinkItems, roundStatusRow, multiRound) {
  // Runden-Sicht der Order für Timer/Ampel: bar-Status + bar*At + createdAt
  // dieser Runde überschreiben die Order-Felder (computeOrderTimer/colorForOrder
  // lesen barStatus + bar*At + createdAt unverändert).
  const rs = roundStatusRow || null;
  const bStatus = rs ? rs.status : (Number(round) === 1 ? (o.barStatus || 'pending') : 'pending');
  const tileOrder = {
    ...o,
    barStatus:      bStatus,
    barPreparingAt: rs ? rs.preparingAt : o.barPreparingAt,
    barReadyAt:     rs ? rs.readyAt : o.barReadyAt,
    createdAt:      (rs && rs.createdAt) || o.createdAt
  };
  const timer  = computeOrderTimer('bar', tileOrder);
  const colCls = colorForOrder('bar', tileOrder, timer.mins, timer.mode);
  const id     = String(o.id || '');
  const next   = nextBarAction(o, bStatus);
  const tileKey = id + '__' + round;

  const orderNum = htmlEscape(o.orderNumber || o.order_number || ('#' + id.slice(0, 6)));
  const customer = htmlEscape(o.customerName || o.customer_name || '—');
  const pickup   = fmtTime(o.pickupTime || o.pickup_time);
  const note     = (o.notes || o.special_requests) ? htmlEscape(o.notes || o.special_requests) : '';

  // service_type-Badge (Session 20 / F2d, S61 erweitert um delivery) — siehe kds.js
  const svcType = o.serviceType || 'takeaway';
  const isDineIn   = svcType === 'dine-in';
  const isDelivery = svcType === 'delivery';
  const tableName = (() => {
    if (!isDineIn || !o.tableId) return '';
    const state = getState();
    const t = (state.tables || []).find(x => x.id === o.tableId);
    return t ? (t.name || o.tableId) : o.tableId;
  })();
  let svcBadge;
  if (isDineIn) {
    svcBadge = `<span class="bar-svc-badge bar-svc-dine-in">🍽️ Tisch ${htmlEscape(tableName)}</span>`;
  } else if (isDelivery) {
    // S61 — Lieferung-Badge mit Zonen-Hinweis (Liefergebiet), Fallback city.
    // Volle Adresse + Fahrer-Zuweisung kommt ins Liefer-Cockpit (S62).
    // S62 HF10 — wenn der Disponent „Zuweisen" geklickt hat, wird der Badge
    // zu „🛵 FAHRER WARTET" umgeschaltet — prominentes Signal für die Theke.
    const da = o.deliveryAddress || o.delivery_address || null;
    const zoneHint = da
      ? (da.zone_name ? ` · ${htmlEscape(da.zone_name)}` : (da.city ? ` · ${htmlEscape(da.city)}` : ''))
      : '';
    const driverWaiting = o.deliveryStatus === 'assigned' || o.deliveryStatus === 'out_for_delivery';
    svcBadge = driverWaiting
      ? `<span class="bar-svc-badge bar-svc-delivery bar-svc-driver-waiting" title="Fahrer ist zugewiesen und wartet — Priorität!">🛵 FAHRER WARTET${zoneHint}</span>`
      : `<span class="bar-svc-badge bar-svc-delivery">🛵 Lieferung${zoneHint}</span>`;
  } else {
    svcBadge = `<span class="bar-svc-badge bar-svc-takeaway">🥡 To-Go</span>`;
  }

  // Phase 17.4 (Session 22) — Reservierungs-Vorbestellung-Badge analog zum KDS.
  const isReservation = o.source === 'reservation' || !!o.reservationId;
  const reservationBadge = isReservation
    ? `<span class="bar-svc-badge bar-svc-reservation" title="Aus Reservierung übernommen">📅 Vorbestellung · ${htmlEscape(pickup || '—')}</span>`
    : '';

  // S60 FIX.3 — Pre-Order-Badge analog zum KDS.
  const preOrderMs = pickupAtMs(o);
  const isPreOrder = preOrderMs !== null && preOrderMs > Date.now();
  const preOrderMinsAhead = isPreOrder ? Math.max(0, Math.round((preOrderMs - Date.now()) / 60000)) : 0;
  const preOrderBadge = isPreOrder && !isReservation
    ? `<span class="bar-svc-badge bar-svc-preorder" title="Wird in ${preOrderMinsAhead} Min scharf">🟣 Pre-Order · in ${preOrderMinsAhead} Min</span>`
    : '';

  // S61 — Lieferadresse nicht im Theken-Display rendern. Volle Adresse +
  // Fahrer-Zuweisung kommt in das Liefer-Cockpit (S62). Kontext durch den
  // Stadt-Hinweis im Service-Badge oben.

  // Felder pro ID — analog zum KDS, eigene CSS-Klassen für Theken-Optik.
  // Phase 18: waitTime kommt aus timer.label (waiting → "Service in X min").
  const pickupLabel = isDineIn ? 'Tisch' : (isDelivery ? 'Lieferung' : 'Abholung');
  const pickupValue = isDineIn ? htmlEscape(tableName) : (pickup || '—');
  // S129 — Operator-Attribution-Badges (Single-Source aus operator-ui).
  const operatorBadges = renderOperatorBadges(o);
  const fieldHtml = {
    orderNumber: `<div class="bar-tile-num">${orderNum} ${svcBadge}${reservationBadge}${preOrderBadge}${operatorBadges}</div>`,
    waitTime:    `<div class="bar-tile-wait">${htmlEscape(timer.label)}</div>`,
    customer:    `<div class="bar-tile-line"><strong>${customer}</strong></div>`,
    pickup:      `<div class="bar-tile-line">${pickupLabel}: <strong>${pickupValue}</strong></div>`,
    status:      `<div class="bar-tile-status">${barStatusLabel(o, bStatus)}</div>`,
    note:        note ? `<div class="bar-note">📝 ${note}</div>` : ''
  };
  const orderedHtml = renderConfiguredFields('bar', fieldHtml);

  // Items wie im KDS rendern — Customizations zeigen, damit das Theken-Team
  // sieht "ohne Eis", "Hafermilch" etc. formatCustomizationsByCategory liefert
  // bei category='Getränke' eine sinnvolle Reihenfolge (Default-Mapping).
  // S154 — Bauanleitung öffnet sich per Klick auf die Position (kein extra Toggle).
  const ingFlagOn = !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb
                       && window.App && window.App.nutritionCache);
  // S196 (Jonas) — Positions-Kommentar nur zeigen, wenn Display-Feld „Notiz" an ist.
  const showItemNote = isDisplayFieldVisible('bar', 'itemNote');
  const renderBarLi = (it, idx) => {
    const qty  = Number(it.qty || it.quantity || 1);
    const name = htmlEscape(it.name || it.nameSnapshot || it.name_snapshot || it.title || 'Getränk');
    const cat  = detectCategory(it);
    let cust = '';
    if (it.customizations) {
      const lines = formatCustomizationsByCategory(it.customizations, cat);
      if (lines && lines.length) {
        cust = lines.map(line => `<div class="bar-item-cust-line">${htmlEscape(line)}</div>`).join('');
      }
    }
    const itemKey = String(it.id || (tileKey + ':' + idx));
    const showBuild = ingFlagOn && _barBuildExpanded.has(itemKey);
    let build = '';
    if (showBuild) {
      const mid = it.menuItemId || it.menu_item_id;
      let steps = null;
      try { steps = window.App.nutritionCache.buildStepsSync(mid, it.customizations || {}); } catch (_) {}
      build = renderBarBuildHtml(steps);
    }
    const clickAttr = ingFlagOn
      ? ` onclick="barToggleBuild('${htmlEscape(itemKey)}')" style="cursor:pointer;" title="${showBuild ? 'Bauanleitung ausblenden' : '🔨 Bauanleitung anzeigen'}"`
      : '';
    // S196 Cut b1 — Positions-Kommentar (order_items.notes) auch an der Theke zeigen.
    const noteRaw = it.notes != null ? it.notes : it.note;
    const noteHtml = (showItemNote && noteRaw && String(noteRaw).trim())
      ? `<div class="bar-item-note">📝 ${htmlEscape(String(noteRaw).trim())}</div>`
      : '';
    return `
      <li class="bar-item${showBuild ? ' bar-item-building' : ''}"${clickAttr}>
        <span class="bar-item-qty">${qty}×</span>
        <span class="bar-item-name">${name}</span>${ingFlagOn ? ' <span class="hint" style="font-size:.66rem;">🔨</span>' : ''}
        ${cust ? `<div class="bar-item-cust">${cust}</div>` : ''}
        ${noteHtml}
        ${build}
      </li>
    `;
  };
  // S139 P5 — KEIN Dimmen mehr. Diese Kachel zeigt nur die Getränke IHRER Runde;
  // bei mehreren Runden ein Batch-Header „Runde N" zur Orientierung.
  const header = (multiRound && round != null) ? `<li class="bar-round-header">Runde ${round}</li>` : '';
  const itemsHtml = header + drinkItems.map((it, idx) => renderBarLi(it, idx)).join('');

  const preOrderCls = isPreOrder ? 'bar-tile-preorder' : '';
  // S139 P5 — data-tile = order__round; Button setzt den Status DIESER Runde.
  return `
    <div class="bar-tile ${colCls} ${preOrderCls}" data-id="${htmlEscape(id)}" data-tile="${htmlEscape(tileKey)}">
      <div class="bar-tile-fields">${orderedHtml}</div>
      ${(isDineIn && o.occupancyId)
          ? renderGuestNotesHtml(notesForRound(guestNotes(), o.occupancyId, drinkItems || []), { cls: 'bar' })
          : ''}
      <ul class="bar-items">${itemsHtml}</ul>
      ${next ? `
        <button class="bar-next-btn" onclick="barNextRoundStatus('${htmlEscape(id)}', ${round})">
          ${next.label}
        </button>
      ` : ''}
    </div>
  `;
}

// — Haupt-Renderer ————————————————————————————
export function renderBar() {
  const root = document.getElementById('view-bar');
  if (!root) return;

  const orders = activeOrders();
  const state = getState();

  // S139 P5 — eine Kachel je (Order, Runde-mit-Getränken). Spiegel von kds.js:
  // Items nach Runde gruppieren, je Runde die Getränke filtern → eine Tile pro
  // Runde mit Getränken. Runden-Status (per Runde×Kanal=bar) parallel laden.
  const tilesData = [];
  let hasUnloaded = false;

  for (const o of orders) {
    const id = String(o.id || '');
    if (!hasCachedRoundStatus(id) && !hasPendingRoundStatus(id)) {
      fetchRoundStatusFor(id).then(() => {
        if (document.body.classList.contains('bar-active')) renderBar();
      }).catch(err => console.warn('[bar] renderBar after fetchRoundStatusFor failed:', err));
    }
    const inlineItems = Array.isArray(o.items) ? o.items : null;
    const cached = getCachedItems(id);
    const items = (inlineItems && inlineItems.length) ? inlineItems : (cached || null);
    if (items === null) {
      hasUnloaded = true;
      fetchItemsFor(id).then(() => {
        if (document.body.classList.contains('bar-active')) renderBar();
      }).catch(err => console.warn('[bar] renderBar after fetchItemsFor failed:', err));
      tilesData.push({ order: o, round: null, drinkItems: null, roundStatus: null, multiRound: false });  // Skeleton
      continue;
    }
    const drinkGroups = groupItemsByRound(items)
      .map(g => ({ round: g.round, items: g.items.filter(isDrinkItem) }))
      .filter(g => g.items.length > 0);
    if (drinkGroups.length === 0) continue;  // Order ohne Drinks → keine Tile
    const multiRound = drinkGroups.length > 1;
    // S196-Fix (Jonas) — Runden absteigend (älteste rechts), konsistent mit dem
    // Order-Sort; analog KDS.
    for (const g of drinkGroups.slice().reverse()) {
      tilesData.push({
        order: o, round: g.round, drinkItems: g.items,
        roundStatus: roundChannelStatus(id, g.round, 'bar'), multiRound
      });
    }
  }

  // Counts pro RUNDEN-KACHEL nach deren Theken-Status (jede Runde = Arbeitseinheit).
  const drinkOrders = tilesData.filter(t => t.round !== null && t.drinkItems && t.drinkItems.length > 0);
  const counts = drinkOrders.reduce((acc, t) => {
    const o = t.order;
    const b = (t.roundStatus && t.roundStatus.status)
      || (Number(t.round) === 1 ? (o.barStatus || 'pending') : 'pending');
    const bucket = barCountBucket(o.status, b);
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

  const tilesHtml = drinkOrders.length === 0
    ? `<div class="bar-empty">
         <div class="bar-empty-icon">🥤</div>
         <div class="bar-empty-text">${hasUnloaded ? 'Lade Bestellungen…' : 'Keine Getränke-Bestellungen.'}</div>
         <div class="bar-empty-hint">${hasUnloaded ? '' : 'Sobald eine Bestellung mit Getränken reinkommt, taucht sie hier auf.'}</div>
       </div>`
    : drinkOrders.map(t => renderTile(t.order, t.round, t.drinkItems, t.roundStatus, t.multiRound)).join('');

  // S60 W4.12 — Walk-In-ohne-Order-Reminder (analog KDS) — nutzt bestehendes `state` (Z. 296)
  const stateOrdersWithOcc = new Set((state.orders || []).filter(o => o.occupancyId).map(o => o.occupancyId));
  const lonelyWalkIns = (state.tableOccupancies || []).filter(o => {
    if (o.deletedAt || o.status !== 'active' || !o.startedAt) return false;
    if (stateOrdersWithOcc.has(o.id)) return false;
    const elapsed = (Date.now() - new Date(o.startedAt).getTime()) / 60000;
    return elapsed >= 20;
  });
  const walkInBanner = lonelyWalkIns.length > 0 ? `
    <div class="bar-walkin-reminder" style="background:#fff4d6;color:#7a4a10;padding:8px 14px;border-bottom:2px solid #d97a2c;font-size:.92rem;display:flex;gap:14px;align-items:center;flex-wrap:wrap;">
      <strong>🪑 ${lonelyWalkIns.length} Walk-In${lonelyWalkIns.length>1?'s':''} ohne Bestellung &gt; 20 Min:</strong>
      ${lonelyWalkIns.slice(0, 5).map(o => {
        const t = (state.tables || []).find(t => t.id === o.tableId);
        const name = (t && t.name) || o.tableId || '?';
        const min = Math.round((Date.now() - new Date(o.startedAt).getTime()) / 60000);
        return `<span>Tisch ${(window.htmlEscape ? window.htmlEscape(String(name)) : String(name))} · ${o.guests}P · ${min} Min</span>`;
      }).join(' · ')}
      ${lonelyWalkIns.length > 5 ? `<span>+ ${lonelyWalkIns.length - 5} weitere</span>` : ''}
    </div>` : '';

  root.innerHTML = `
    <div class="bar-topbar">
      <div class="bar-brand">
        <span class="bar-brand-mark">G&amp;G</span>
        <span>Theken-Display</span>
      </div>
      <div class="bar-counts">
        <span class="bar-count-pill pending">Neu: ${counts.pending || 0}</span>
        <span class="bar-count-pill confirmed">Angenommen: ${counts.confirmed || 0}</span>
        <span class="bar-count-pill preparing">In Zubereitung: ${counts.preparing || 0}</span>
        <span class="bar-count-pill ready">Bereit: ${counts.ready || 0}</span>
      </div>
      <div class="bar-actions">
        <button class="btn-bar-ghost" onclick="barToggleDateFilter()"
                title="${_barShowAllDates ? 'Nur heute zeigen' : 'Alle aktiven Tage zeigen'}">
          ${_barShowAllDates ? '📅 Alle' : '📅 Heute'}
        </button>
        <button class="btn-bar-ghost" onclick="barToggleFullscreen()" title="Vollbild">⛶</button>
        <button class="btn-bar-ghost" onclick="switchView('admin')">← Cockpit</button>
      </div>
    </div>
    ${walkInBanner}
    <div class="bar-grid">${tilesHtml}</div>
  `;
}

// — Public API: Status-Next (analog KDS) ——————————

export async function barNextRoundStatus(id, round) {
  const order = (getState().orders || []).find(o => o.id === id);
  if (!order) return;
  const rs = roundChannelStatus(id, round, 'bar');
  const roundStatus = rs ? rs.status : (Number(round) === 1 ? (order.barStatus || 'pending') : 'pending');
  const next = nextBarAction(order, roundStatus);
  if (!next) return;
  const tile = document.querySelector(`.bar-tile[data-tile="${CSS.escape(id + '__' + round)}"]`);
  if (tile) tile.classList.add('bar-tile-pending-action');
  try {
    if (next.kind === 'channel') {
      const storage = window.App && window.App.storage && window.App.storage.orders;
      if (!storage || typeof storage.setRoundChannelStatus !== 'function') {
        throw new Error('Runden-Status-API nicht verfügbar');
      }
      const updated = await storage.setRoundChannelStatus(id, round, next.channel, next.status);
      invalidateRoundStatus(id);
      if (updated && window.App.events) {
        await window.App.events.emit('order:status_changed', {
          order: updated, channel: next.channel, channelStatus: next.status
        });
      } else {
        renderBar();
      }
    } else {
      if (typeof window.orderSetStatus !== 'function') {
        throw new Error('Bestellungs-Modul nicht geladen');
      }
      await window.orderSetStatus(id, next.status);
    }
  } catch (e) {
    if (tile) tile.classList.remove('bar-tile-pending-action');
    showToast('Status-Wechsel fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// — Lifecycle —————————————————————————————
let _barInitialized = false;
let _barTicker = null;

export function enterBarView() {
  document.body.classList.add('bar-active');
  renderBar();
  installBarListenersOnce();
  // S253 — Hinweise laden und auf Änderungen hören (siehe kds.js).
  if (!_barNotesOff) {
    _barNotesOff = initGuestNotes(() => {
      if (document.body.classList.contains('bar-active')) renderBar();
    });
  }
  try {
    if (!window.location.hash.includes('#bar')) {
      const url = window.location.pathname + window.location.search + '#bar';
      window.history.replaceState(null, '', url);
    }
  } catch (_) {}
  try {
    const orders = activeOrders();
    const orderIds = orders.map(o => String(o.id || '')).filter(Boolean);
    // S60 PERF.1 — Batch-Loader statt N+1.
    batchFetchItemsFor(orderIds).then(() => {
      if (document.body.classList.contains('bar-active')) renderBar();
    }).catch(err => console.warn('[bar] batch pre-fetch failed:', err));
    // S139 P5 — Runden-Status im selben Schwung vorladen.
    batchFetchRoundStatusFor(orderIds).then(() => {
      if (document.body.classList.contains('bar-active')) renderBar();
    }).catch(err => console.warn('[bar] round-status pre-fetch failed:', err));
  } catch (e) { console.warn('[bar] pre-fetch:', e); }
  if (_barTicker) clearInterval(_barTicker);
  _barTicker = setInterval(() => {
    if (document.body.classList.contains('bar-active')) renderBar();
  }, 30000);
}

export function exitBarView() {
  document.body.classList.remove('bar-active');
  if (_barTicker) { clearInterval(_barTicker); _barTicker = null; }
  try {
    if (window.location.hash.includes('#bar')) {
      const cleaned = window.location.hash.replace(/#bar/g, '');
      const url = window.location.pathname + window.location.search + cleaned;
      window.history.replaceState(null, '', url);
    }
  } catch (_) {}
}

function installBarListenersOnce() {
  if (_barInitialized) return;
  _barInitialized = true;
  const App = window.App;
  if (!App || !App.events) return;

  // S60 PERF.3 — barScheduleRender: zentrales Debouncing für Theke (analog KDS).
  // Vermeidet Doppel-Renders + reduziert Bursts auf 1 Render pro 150ms.
  let _barRenderTimer = null;
  function barScheduleRender() {
    if (!document.body.classList.contains('bar-active')) return;
    if (_barRenderTimer) return;
    _barRenderTimer = setTimeout(() => {
      _barRenderTimer = null;
      if (document.body.classList.contains('bar-active')) renderBar();
    }, 150);
  }

  App.events.on('order:realtime', payload => {
    if (!document.body.classList.contains('bar-active')) return;
    try {
      const newId = payload && payload.new && payload.new.id;
      const oldId = payload && payload.old && payload.old.id;
      if (newId) { invalidateItems(String(newId)); invalidateRoundStatus(String(newId)); }
      if (oldId && oldId !== newId) { invalidateItems(String(oldId)); invalidateRoundStatus(String(oldId)); }
      if (payload && payload.eventType === 'INSERT' && payload.new
          && payload.new.status === 'pending'
          && typeof window.playOrderPing === 'function') {
        if (!document.body.classList.contains('kds-active')) {
          window.playOrderPing();
        }
      }
      // S60 PERF.3 — Items-Prefetch und Render in EINEM Flow (vorher: 2 Renders)
      if (newId) {
        fetchItemsFor(String(newId)).then(() => barScheduleRender())
          .catch(err => console.warn('[bar] realtime item refresh failed:', err));
      } else {
        barScheduleRender();
      }
    } catch (e) {
      console.warn('[bar] realtime handler:', e);
    }
  });
  App.events.on('order:status_changed', payload => {
    const o = payload && payload.order;
    if (o && o.id) { invalidateItems(String(o.id)); invalidateRoundStatus(String(o.id)); }
    barScheduleRender();
  });
  App.events.on('order:cancelled', payload => {
    const o = payload && payload.order;
    if (o && o.id) { invalidateItems(String(o.id)); invalidateRoundStatus(String(o.id)); }
    barScheduleRender();
  });
  // S139 P5 — Runden-Status-Realtime (analog KDS).
  App.events.on('order:round_status:realtime', payload => {
    if (!document.body.classList.contains('bar-active')) return;
    const row = (payload && (payload.new || payload.old)) || null;
    const oid = row && (row.order_id || row.orderId);
    if (!oid) { barScheduleRender(); return; }
    invalidateRoundStatus(String(oid));
    fetchRoundStatusFor(String(oid)).then(() => barScheduleRender())
      .catch(err => console.warn('[bar] round-status realtime refresh failed:', err));
  });
  // S62 HF10 — Liefer-Order „scharf" auch in der Theke
  App.events.on('order:delivery_assigned', payload => {
    const o = payload && payload.order;
    if (!o || !document.body.classList.contains('bar-active')) return;
    if (o.id) invalidateItems(String(o.id));
    if (typeof window.playOrderPing === 'function') {
      try { window.playOrderPing(); } catch (_) {}
    }
    barScheduleRender();
  });
  App.events.on('order:created', payload => {
    if (payload && payload.id) {
      fetchItemsFor(String(payload.id)).then(() => barScheduleRender())
        .catch(err => console.warn('[bar] order:created item fetch failed:', err));
    } else {
      barScheduleRender();
    }
  });
}

// S154 — Bauanleitung einer Position per Klick aufs Getränk ein-/ausklappen.
export function barToggleBuild(itemKey) {
  if (!itemKey) return;
  if (_barBuildExpanded.has(itemKey)) {
    _barBuildExpanded.delete(itemKey);
    renderBar();
    return;
  }
  _barBuildExpanded.add(itemKey);
  const cache = window.App && window.App.nutritionCache;
  if (cache && cache.ensureLoaded) {
    cache.ensureLoaded().then(() => renderBar()).catch(() => renderBar());
  } else {
    renderBar();
  }
}

export function barToggleFullscreen() {
  const doc = document.documentElement;
  if (!document.fullscreenElement) {
    if (doc.requestFullscreen) doc.requestFullscreen().catch(() => {});
  } else {
    if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
  }
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderBar, enterBarView, exitBarView,
    barRefresh: renderBar,
    barNextRoundStatus, barToggleFullscreen,
    barToggleDateFilter, barToggleBuild
  });
}

// -----------------------------------------------------------------------------
// Test-Hooks (vitest) — Pure-Filter + Toggle-State setzen, ohne renderBar/DOM.
// -----------------------------------------------------------------------------
export function _setBarShowAllDates(v) { _barShowAllDates = !!v; }
export { activeOrders as _barActiveOrders };
